import React, { useEffect, useMemo, useState } from 'react';
import { Dialog } from '@headlessui/react';
import { ArrowsUpDownIcon, ChevronUpIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useBook } from '../../../contexts/BookContext';
import { updateBook } from '../../../services/firebase/firestore';

export default function NarrationOrderModal({ isOpen, onClose, spreadId }) {
  const { currentBook, loadBook } = useBook();
  const [leftItems, setLeftItems] = useState([]);
  const [rightItems, setRightItems] = useState([]);

  const spread = useMemo(() => (currentBook?.spreads || {})[spreadId] || null, [currentBook?.spreads, spreadId]);

  // Build initial lists when opened or spread changes
  useEffect(() => {
    if (!isOpen || !spread) return;
    const els = spread.elements || {};
    const entries = Object.entries(els)
      .filter(([_, el]) => el?.type === 'text' && el?.audio?.url)
      .map(([elementId, el]) => ({ elementId, el }));
    if (!entries.length) { setLeftItems([]); setRightItems([]); return; }

    const xs = entries.map(({ el }) => Number(el?.position?.x || 0)).sort((a, b) => a - b);
    const medianX = xs.length ? xs[Math.floor(xs.length / 2)] : 0;
    const left = [];
    const right = [];
    entries.forEach((t) => {
      const x = Number(t.el?.position?.x || 0);
      const title = t.el?.content ? String(t.el.content).slice(0, 60) : 'Text';
      const item = { id: t.elementId, title, order: typeof t.el?.audio?.order === 'number' ? t.el.audio.order : null, pos: t.el?.position || {} };
      if (x <= medianX) left.push(item); else right.push(item);
    });

    const sortByOrderOrPos = (arr) => {
      const allHaveOrder = arr.every(i => typeof i.order === 'number');
      if (allHaveOrder) return arr.slice().sort((a, b) => (a.order || 0) - (b.order || 0));
      return arr.slice().sort((a, b) => {
        const ay = Number(a.pos?.y || 0), by = Number(b.pos?.y || 0);
        if (ay !== by) return ay - by;
        const ax = Number(a.pos?.x || 0), bx = Number(b.pos?.x || 0);
        return ax - bx;
      });
    };

    setLeftItems(sortByOrderOrPos(left));
    setRightItems(sortByOrderOrPos(right));
  }, [isOpen, spread]);

  const move = (list, setList, id, dir) => {
    setList((cur) => {
      const idx = cur.findIndex(i => i.id === id);
      if (idx < 0) return cur;
      const j = dir === 'up' ? idx - 1 : idx + 1;
      if (j < 0 || j >= cur.length) return cur;
      const copy = cur.slice();
      const tmp = copy[idx];
      copy[idx] = copy[j];
      copy[j] = tmp;
      return copy;
    });
  };

  const handleSave = async () => {
    if (!currentBook || !spreadId) return;
    const spreads = { ...(currentBook.spreads || {}) };
    const s = spreads[spreadId];
    if (!s) return;
    const elements = { ...(s.elements || {}) };

    leftItems.forEach((item, i) => {
      if (!elements[item.id]) return;
      const el = elements[item.id];
      elements[item.id] = { ...el, audio: { ...(el.audio || {}), order: i } };
    });
    rightItems.forEach((item, i) => {
      if (!elements[item.id]) return;
      const el = elements[item.id];
      // Offset right side by left length to keep a single order sequence if needed
      elements[item.id] = { ...el, audio: { ...(el.audio || {}), order: leftItems.length + i } };
    });

    spreads[spreadId] = { ...s, elements };
    await updateBook(currentBook.id, { ...currentBook, spreads });
    try { await loadBook(currentBook.id); } catch {}
    onClose?.();
  };

  const List = ({ title, items, setItems }) => (
    <div className="w-full">
      <div className="text-sm font-medium text-gray-700 mb-2 flex items-center gap-2">
        <ArrowsUpDownIcon className="w-4 h-4 text-gray-400" /> {title}
      </div>
      <div className="space-y-2 max-h-64 overflow-auto pr-1">
        {items.map((it, idx) => (
          <div key={it.id} className="flex items-center gap-2 p-2 rounded border bg-white">
            <span className="text-xs text-gray-500 w-6 text-right">{idx + 1}</span>
            <div className="flex-1 text-sm truncate" title={it.title}>{it.title}</div>
            <div className="flex items-center gap-1">
              <button
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                onClick={() => move(items, setItems, it.id, 'up')}
                disabled={idx === 0}
                title="Move up"
              >
                <ChevronUpIcon className="w-4 h-4 text-gray-600" />
              </button>
              <button
                className="p-1 rounded hover:bg-gray-100 disabled:opacity-40"
                onClick={() => move(items, setItems, it.id, 'down')}
                disabled={idx === items.length - 1}
                title="Move down"
              >
                <ChevronDownIcon className="w-4 h-4 text-gray-600" />
              </button>
            </div>
          </div>
        ))}
        {items.length === 0 && (
          <div className="text-xs text-gray-400">No files yet...</div>
        )}
      </div>
    </div>
  );

  return (
    <Dialog open={!!isOpen} onClose={onClose} className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen p-4">
        <Dialog.Overlay className="fixed inset-0 bg-black/30" />
        <div className="relative bg-white rounded-lg max-w-2xl w-full p-6">
          <div className="absolute top-4 right-4">
            <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          <Dialog.Title className="text-lg font-medium text-gray-900 mb-4">Narration Order for {spreadId}</Dialog.Title>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <List title="Left Page" items={leftItems} setItems={setLeftItems} />
            <List title="Right Page" items={rightItems} setItems={setRightItems} />
          </div>

          <div className="mt-6 flex justify-end gap-3">
            <button onClick={onClose} className="px-4 py-2 text-sm rounded-md border">Cancel</button>
            <button onClick={handleSave} className="px-4 py-2 text-sm rounded-md bg-indigo-600 text-white hover:bg-indigo-700">Save Order</button>
          </div>
        </div>
      </div>
    </Dialog>
  );
}
